import React, { useState, useEffect, useMemo } from 'react';
import { getWorkspaceItems, deleteWorkspaceItem } from '../../services/api';

const STATUS_TABS = [
  { key: 'ACTIVE', label: 'All Active', statuses: ['PENDING_EXTRACTION', 'PENDING_COMPLETION', 'PENDING_REVIEW'] },
  { key: 'PENDING_EXTRACTION', label: 'Extracting', statuses: ['PENDING_EXTRACTION'] },
  { key: 'PENDING_COMPLETION', label: 'Needs Input', statuses: ['PENDING_COMPLETION'] },
  { key: 'PENDING_REVIEW', label: 'Review', statuses: ['PENDING_REVIEW'] },
  { key: 'COMPLETED', label: 'Completed', statuses: ['APPROVED', 'REJECTED'] },
];

const STATUS_DISPLAY = {
  PENDING_EXTRACTION: { label: 'Extracting', className: 'pending' },
  PENDING_COMPLETION: { label: 'Needs Input', className: 'review' },
  PENDING_REVIEW: { label: 'Review', className: 'review' },
  APPROVED: { label: 'Approved', className: 'active' },
  REJECTED: { label: 'Rejected', className: 'critical' },
};

const ITEM_TYPE_DISPLAY = {
  DOCUMENT_UPLOAD: { label: 'Document Upload', className: 'review' },
  TEAMING_AGREEMENT: { label: 'Teaming Agreement', className: 'pending' },
  CONTRACT_REQUEST: { label: 'Contract Request', className: 'accepted' },
  GENERAL_TASK: { label: 'General Task', className: 'completed' },
};

function formatDate(iso) {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatTimeAgo(iso) {
  if (!iso) return '';
  const now = new Date();
  const d = new Date(iso);
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return formatDate(iso);
}

function daysUntilExpiry(expiresAt) {
  if (!expiresAt) return null;
  const now = Math.floor(Date.now() / 1000);
  const diff = expiresAt - now;
  if (diff <= 0) return 0;
  return Math.ceil(diff / 86400);
}

function Workspace({ onNavigate, isAdmin }) {
  const [activeTab, setActiveTab] = useState('ACTIVE');
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function fetchItems() {
      setLoading(true);
      setError('');
      try {
        const data = await getWorkspaceItems();
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load workspace items');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchItems();
    return () => { cancelled = true; };
  }, []);

  const tabDef = STATUS_TABS.find((t) => t.key === activeTab) || STATUS_TABS[0];

  const filtered = useMemo(() => {
    let result = items.filter((d) => tabDef.statuses.includes(d.status));

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((d) =>
        (d.title || d.documentName || '').toLowerCase().includes(q) ||
        (d.uploadedBy || '').toLowerCase().includes(q) ||
        (d.itemType || '').toLowerCase().includes(q) ||
        (d.department || '').toLowerCase().includes(q)
      );
    }

    // Sort: active items by createdAt desc, completed by reviewedAt desc
    result.sort((a, b) => {
      const dateA = new Date(activeTab === 'COMPLETED' ? (a.reviewedAt || a.createdAt) : a.createdAt);
      const dateB = new Date(activeTab === 'COMPLETED' ? (b.reviewedAt || b.createdAt) : b.createdAt);
      return dateB - dateA;
    });

    return result;
  }, [items, activeTab, tabDef.statuses, searchQuery]);

  const tabCounts = useMemo(() => {
    const counts = {};
    STATUS_TABS.forEach(({ key, statuses }) => {
      counts[key] = items.filter((d) => statuses.includes(d.status)).length;
    });
    return counts;
  }, [items]);

  async function handleDelete(item) {
    const confirmed = window.confirm(
      `Delete "${item.title || item.documentName}"?\n\nThis will remove the workspace item and its uploaded document. This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await deleteWorkspaceItem(item.workspaceId);
      setItems((prev) => prev.filter((i) => i.workspaceId !== item.workspaceId));
    } catch (err) {
      setError(err.message || 'Failed to delete workspace item');
    }
  }

  function getActionButtons(item) {
    const actions = [];

    actions.push(
      <button
        key="view"
        className="table-action-btn"
        title="View"
        onClick={() => onNavigate('workspace-detail', { workspaceId: item.workspaceId })}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
    );

    if (item.status === 'PENDING_COMPLETION') {
      actions.push(
        <button
          key="edit"
          className="btn btn-sm btn-outline"
          onClick={() => onNavigate('workspace-detail', { workspaceId: item.workspaceId })}
        >
          Complete
        </button>
      );
    }

    if (item.status === 'PENDING_REVIEW') {
      actions.push(
        <button
          key="review"
          className="btn btn-sm btn-primary"
          onClick={() => onNavigate('workspace-detail', { workspaceId: item.workspaceId })}
        >
          Review
        </button>
      );
    }

    if (item.status !== 'APPROVED' && item.status !== 'REJECTED') {
      actions.push(
        <button
          key="delete"
          className="table-action-btn"
          title="Delete"
          onClick={() => handleDelete(item)}
          style={{ color: 'var(--bcore-text-dim)' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
      );
    }

    return actions;
  }

  const isCompletedTab = activeTab === 'COMPLETED';

  return (
    <div>
      {/* Toolbar */}
      <div className="page-toolbar">
        <div className="page-toolbar-left">
          <div className="search-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search workspace..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="page-toolbar-right">
          <button
            className="btn btn-primary"
            onClick={() => onNavigate('document-upload')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            Upload Document
          </button>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="tab-bar">
        {STATUS_TABS.map(({ key, label }) => (
          <button
            key={key}
            className={`tab-btn${activeTab === key ? ' active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
            <span className="count">{tabCounts[key] || 0}</span>
          </button>
        ))}
      </div>

      {/* Error State */}
      {error && (
        <div className="form-api-error">{error}</div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: 'var(--bcore-text-dim)',
          fontSize: '13px',
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}>
          Loading workspace items...
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filtered.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'var(--bcore-text-dim)',
          fontSize: '13px',
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: '16px', opacity: 0.4 }}>
            {isCompletedTab ? (
              <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>
            ) : (
              <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>
            )}
          </svg>
          <div style={{ textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
            {isCompletedTab ? 'No completed items' :
              items.length === 0 ? 'No items in workspace' : 'No matching items'}
          </div>
          <div style={{ fontSize: '12px' }}>
            {isCompletedTab ? 'Completed items will appear here for 30 days' :
              items.length === 0
                ? 'Upload a document or create a task to get started'
                : 'Try adjusting your search or filters'}
          </div>
        </div>
      )}

      {/* Data Table */}
      {!loading && filtered.length > 0 && (
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Uploaded By</th>
                <th>Created</th>
                <th>Status</th>
                {isCompletedTab && <th>Expires</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const statusInfo = STATUS_DISPLAY[item.status] || { label: item.status, className: '' };
                const typeInfo = ITEM_TYPE_DISPLAY[item.itemType] || { label: item.itemType || 'Unknown', className: '' };
                const expiryDays = isCompletedTab ? daysUntilExpiry(item.expiresAt) : null;

                return (
                  <tr
                    key={item.workspaceId}
                    style={isCompletedTab ? { opacity: 0.7 } : undefined}
                  >
                    <td style={{ maxWidth: '300px' }}>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.title || item.documentName || 'Untitled'}
                      </div>
                    </td>
                    <td>
                      <span className={`badge-status ${typeInfo.className}`} style={{ fontSize: '10px' }}>
                        {typeInfo.label}
                      </span>
                    </td>
                    <td>{item.uploadedBy || '--'}</td>
                    <td title={item.createdAt}>{formatTimeAgo(item.createdAt)}</td>
                    <td>
                      <span className={`badge-status ${statusInfo.className}`}>
                        <span className="badge-dot"></span>
                        {statusInfo.label}
                      </span>
                    </td>
                    {isCompletedTab && (
                      <td>
                        {expiryDays !== null ? (
                          <span style={{
                            fontSize: '11px',
                            color: expiryDays <= 7 ? 'var(--status-critical)' : 'var(--bcore-text-dim)',
                          }}>
                            {expiryDays <= 0 ? 'Expiring...' : `${expiryDays}d left`}
                          </span>
                        ) : (
                          <span style={{ fontSize: '11px', color: 'var(--bcore-text-dim)' }}>--</span>
                        )}
                      </td>
                    )}
                    <td>
                      <div className="table-actions">
                        {getActionButtons(item)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Workspace;
